'use client'

import type {
  ProductListingDataContextProps,
  ProductListingDataProviderProps,
} from '@maestro/commerce-blocks/providers/product-listing-data/types'
import type { Price, Stock } from '@maestro/data-layer'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:commerce-blocks:listing-data')

const defaultValue: ProductListingDataContextProps = {
  isLoadingDataSummary: false,
  getProductStock: () => undefined,
  getProductPrice: () => undefined,
}

const ProductListingDataContext =
  createContext<ProductListingDataContextProps>(defaultValue)

export const ProductListingDataProvider = ({
  children,
  value,
}: {
  children: React.ReactNode
  value?: ProductListingDataProviderProps
}) => {
  const {
    productIds,
    enabledStockFetch = false,
    enabledStockView = false,
    enabledPriceFetch = false,
    enabledPriceView = false,
    productIdsToStockPromise: stockPromise,
    productIdsToPricePromise: pricePromise,
  } = value ?? {}

  const [productIdsToStockMap, setProductIdsToStockMap] = useState<
    Record<string, Stock>
  >({})

  const [productIdsToPriceMap, setProductIdsToPriceMap] = useState<
    Record<string, Price>
  >({})

  const [isLoading, setIsLoading] = useState(() =>
    Boolean(stockPromise || pricePromise)
  )

  useEffect(() => {
    if (
      (!enabledStockFetch && !enabledPriceFetch) ||
      (!stockPromise && !pricePromise)
    ) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const fetchDataSummary = async () => {
      log.debug(`Loading Product Data: ${productIds?.join(', ')}`)
      setIsLoading(true)

      const [stockResult, priceResult] = await Promise.allSettled([
        stockPromise ?? Promise.resolve({}),
        pricePromise ?? Promise.resolve({}),
      ])

      if (cancelled) {
        return
      }

      if (stockResult.status === 'fulfilled') {
        setProductIdsToStockMap(stockResult.value ?? {})
      } else {
        log.warn('Error fetching product stock data', {
          error: stockResult.reason,
        })
      }

      if (priceResult.status === 'fulfilled') {
        setProductIdsToPriceMap(priceResult.value ?? {})
      } else {
        log.warn('Error fetching product price data', {
          error: priceResult.reason,
        })
      }

      setIsLoading(false)
    }

    fetchDataSummary()

    return () => {
      cancelled = true
    }
  }, [
    stockPromise,
    pricePromise,
    enabledStockFetch,
    productIds,
    enabledPriceFetch,
  ])

  const getProductStock = useCallback(
    (productId: string) =>
      enabledStockView ? productIdsToStockMap?.[productId] : undefined,
    [enabledStockView, productIdsToStockMap]
  )

  const getProductPrice = useCallback(
    (productId: string) =>
      enabledPriceView ? productIdsToPriceMap?.[productId] : undefined,
    [enabledPriceView, productIdsToPriceMap]
  )

  const contextValue = useMemo<ProductListingDataContextProps>(
    () => ({
      isLoadingDataSummary: isLoading,
      getProductStock,
      getProductPrice,
    }),
    [isLoading, getProductStock, getProductPrice]
  )

  return (
    <ProductListingDataContext.Provider value={contextValue}>
      {children}
    </ProductListingDataContext.Provider>
  )
}

export const useProductListingDataContext = () =>
  useContext(ProductListingDataContext)
